import React from 'react';
import { diffAgainstBaseline } from '../libs/listViewDiff';
import { visibleColumns } from '../libs/listViewPresentation';
import {
  fetchResourceBookings,
  fetchViewSnapshot,
  saveViewSnapshot
} from '../libs/listViewRequests';
import { serializeYesplanAPIDate } from '../libs/dateTimeUtilities';
import type {
  ActiveFilter,
  ListViewBooking,
  ListViewConfiguration,
  ListViewDate,
  ListViewDiff,
  ListViewSnapshot
} from '../listview/types';

/* The resource bookings of the selected day, and the markers that come from
 * comparing them against the previous fetch.
 *
 * Every successful fetch is diffed against the previous one to mark bookings
 * deleted in Yesplan and fields whose values changed; those markers last
 * exactly one refresh cycle, after which the fetch that showed them becomes
 * the new comparison baseline itself. The baseline is kept in memory and
 * persisted per user via /viewsnapshot so the markers also work across a page
 * reload.
 *
 * A fetch is only allowed once everything it depends on has actually arrived;
 * readyToFetch below is the single place where those conditions, and the
 * reason for each of them, are written down.
 * */

interface ListViewBookingsOptions {
  host: string;
  viewID: string;
  configuration: ListViewConfiguration;
  date: ListViewDate;
  /* The filters in force: the snapshot stores the fields they read, so a
     booking deleted in Yesplan can still be filtered like a live one. A
     change of filters does not fetch; it is picked up by the next fetch */
  activeFilters: ActiveFilter[];
  /* Whether date is the stored day or still the provisional default; see
     readyToFetch */
  settingsSettled: boolean;
}

interface ListViewBookingsData {
  bookings: ListViewBooking[] | null;
  diff: ListViewDiff | null;
  /* The bookings currently held are the ones for the selected day */
  bookingsFetched: boolean;
  isRefreshing: boolean;
  lastRefreshedAt: string | null;
  refreshFailed: boolean;
  errorCode: number | false;
  refresh: () => void;
}

/* The bookings together with the day they were fetched for, so that "are we
   showing the selected day?" is a question about the data rather than a flag
   that has to be reset by hand on every change of day */
interface LoadedBookings {
  date: string;
  bookings: ListViewBooking[];
  diff: ListViewDiff | null;
}

/* Everything a bookings fetch depends on, in the one place where the fetch is
   allowed to start */
interface BookingsFetchInputs {
  viewID: string;
  settingsSettled: boolean;
  snapshotSettledFor: string | null;
}

/* Whether a bookings fetch may start. Both inputs arrive asynchronously and
 * independently, and neither may be taken at face value before its own
 * request has come back: both have a plausible-looking value before that,
 * which is what makes an ungated fetch fetch the wrong thing rather than
 * nothing:
 *
 *   settingsSettled        the selected day is today until the stored settings
 *                          arrive and replace it. Fetching before that fetches
 *                          a day the user did not ask for, and — the part that
 *                          is not merely a wasted request — advances the
 *                          baseline with it, so the day they do get shown is
 *                          diffed against another day's bookings and comes up
 *                          entirely new. The hook cannot tell "today because
 *                          it is stored" from "today because we don't know
 *                          yet" from the date alone, which is why this arrives
 *                          as its own flag
 *
 *   snapshotSettledFor     the stored baseline is absent until /viewsnapshot
 *                          comes back, and absent reads as "first fetch ever".
 *                          A baseline landing after the first fetch would mute
 *                          this cycle's markers and invent them for the next
 *                          one. Matched against the view's id rather than
 *                          being a bare flag, so switching views cannot fetch
 *                          against the previous view's baseline
 *
 * Both are "the request came back", not "it held something": a first-time
 * user has neither stored settings nor a stored snapshot and gets a 404 for
 * both, and a gate waiting for content would never open for them.
 *
 * The gate therefore starts closed and opens exactly once, which is what makes
 * the initial fetch happen once, with the restored day, and never with today. */
const readyToFetch = ({
  viewID,
  settingsSettled,
  snapshotSettledFor
}: BookingsFetchInputs): boolean => settingsSettled && snapshotSettledFor === viewID;

const useListViewBookings = ({
  host,
  viewID,
  configuration,
  date,
  activeFilters,
  settingsSettled
}: ListViewBookingsOptions): ListViewBookingsData => {
  const [loaded, setLoaded] = React.useState<LoadedBookings | null>(null);
  /* Which view's snapshot request has come back, rather than a bare flag, so
     switching to another view cannot fetch its bookings against the previous
     view's baseline */
  const [snapshotSettledFor, setSnapshotSettledFor] = React.useState<string | null>(
    null
  );
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<string | null>(null);
  const [refreshFailed, setRefreshFailed] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState<number | false>(false);

  /* The previous fetch, to diff the next one against */
  const baselineRef = React.useRef<ListViewSnapshot | null>(null);
  /* Set as soon as a fetch has advanced the baseline, so a late /viewsnapshot
     response can never overwrite it with the stored (older) one */
  const baselineAdvancedRef = React.useRef(false);
  /* Guards against out-of-order responses: only the latest fetch may land */
  const sequenceRef = React.useRef(0);
  const inFlightDateRef = React.useRef<string | null>(null);
  const unmountedRef = React.useRef(false);

  React.useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    []
  );

  const serializedDate = serializeYesplanAPIDate(date);
  const bookingsFetched = loaded !== null && loaded.date === serializedDate;

  /* Deliberately not keyed on the day: a baseline for a different day is kept
     and then rejected by snapshotIsValidBaseline, which is what stops a
     booking of another day from reading as a deletion */
  React.useEffect(() => {
    let cancelled = false;
    baselineRef.current = null;
    baselineAdvancedRef.current = false;
    fetchViewSnapshot(host, viewID, (snapshot, error) => {
      if (cancelled) return;
      if (!error && snapshot && !baselineAdvancedRef.current)
        baselineRef.current = snapshot;
      /* Settled, not restored: a first-time user has no stored snapshot and
         gets a 404. See readyToFetch */
      setSnapshotSettledFor(viewID);
    });
    return () => {
      cancelled = true;
    };
  }, [host, viewID]);

  const fetchBookings = (
    dateToFetch: ListViewDate,
    serializedDateToFetch: string,
    options: { background: boolean }
  ) => {
    sequenceRef.current += 1;
    const sequence = sequenceRef.current;
    inFlightDateRef.current = serializedDateToFetch;
    /* Captured at request time: by the time the response lands the view may
       already be showing a different day, configuration or set of filters */
    const columns = visibleColumns(configuration);
    if (options.background) setIsRefreshing(true);

    fetchResourceBookings(host, dateToFetch, (body, error) => {
      if (unmountedRef.current || sequence !== sequenceRef.current) return;
      inFlightDateRef.current = null;
      if (error || !body) {
        if (options.background) {
          setIsRefreshing(false);
          setRefreshFailed(true);
        } else setErrorCode(error ? error.code : 500);
        return;
      }
      const { diff, bookings, nextSnapshot } = diffAgainstBaseline({
        fetchedBookings: body.bookings,
        baseline: baselineRef.current,
        columns,
        activeFilters,
        serializedDate: serializedDateToFetch,
        takenAt: new Date().toISOString()
      });
      baselineRef.current = nextSnapshot;
      baselineAdvancedRef.current = true;
      saveViewSnapshot(host, viewID, nextSnapshot);
      setLoaded({ date: serializedDateToFetch, bookings, diff });
      setIsRefreshing(false);
      setRefreshFailed(false);
      setErrorCode(false);
      setLastRefreshedAt(new Date().toISOString());
    });
  };

  const ready = readyToFetch({ viewID, settingsSettled, snapshotSettledFor });

  /* Fires once when the gate opens — with the restored day, never with today —
     and then once per change of day. The two guards make a second fetch for a
     day already in flight or already loaded impossible regardless of what
     re-runs the effect. */
  React.useEffect(() => {
    if (!ready) return;
    if (inFlightDateRef.current === serializedDate) return;
    if (loaded !== null && loaded.date === serializedDate) return;
    fetchBookings(date, serializedDate, { background: false });
  }, [ready, serializedDate, loaded]);

  return {
    bookings: loaded ? loaded.bookings : null,
    diff: loaded ? loaded.diff : null,
    bookingsFetched,
    isRefreshing,
    lastRefreshedAt,
    refreshFailed,
    errorCode,
    refresh: () => {
      /* Nothing to refresh before the initial load completed (or when it
         failed), and a refresh already in flight is not doubled up */
      if (!bookingsFetched || isRefreshing || errorCode) return;
      fetchBookings(date, serializedDate, { background: true });
    }
  };
};

export default useListViewBookings;
